import 'tippy.js/dist/tippy.css'
import 'tippy.js/animations/shift-away.css'
import 'tippy.js/themes/light-border.css'

import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  type PaginationState,
  useReactTable,
} from '@tanstack/react-table'
import { ExternalLinkIcon, Search } from 'lucide-react'
import React, { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import tippy from 'tippy.js'
import { useDebounceValue } from 'usehooks-ts'

import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from 'components/ui/sheet'
import { partnersEmployees } from 'rails_routes'
import { Autocomplete, Checkbox, FilterPillButton, FiltersDropdown, Pagination, StepNav, Table, TextInput, TextLink } from 'slab'
import { useGetPartnersEmployers, useGetPartnersPartnerIdPartnerAdmins, usePatchPartnersEmployersIdAssignee } from 'utils/web_api'
import { type GetPartnersEmployersParams, type PartnersEmployer as Employer, PartnersEmployerStatus, type PartnersPartnerAdmin } from 'utils/web_api.schemas'
import { useWindowLocation } from 'utils/windowLocation'

import PartnersEmployersTableLoadingSkeleton from './PartnersEmployersTableLoadingSkeleton'
import ReferralPageLinkButton from './ReferralPageLinkButton'
import RequestAQuoteButton from './RequestAQuoteButton'
import { formatMoney } from '../../utils'
import CopyButton from '../reusable/CopyButton'

const handleStatusHover = (event: React.MouseEvent<HTMLDivElement>, statusTip: string) => {
  event.stopPropagation()
  tippy(event.target as HTMLElement, {
    content: statusTip,
    theme: 'light-border',
    animation: 'shift-away',
    arrow: true,
    placement: 'bottom',
    trigger: 'mouseenter',
    delay: 500,
  })
}

const columns: ColumnDef<Employer>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row }) => {
      return <div className='text-gray-900 max-w-[400px] truncate' title={row.getValue('name')}>{row.getValue('name')}</div>
    },
  },
  {
    accessorKey: 'assignee',
    header: 'Assignee',
    enableHiding: true,
    cell: ({ row }) => {
      const employer = row.original as Employer
      return <div>{employer.assignee?.name}</div>
    }
  },
  {
    accessorKey: 'location',
    header: 'Location',
  },
  {
    accessorKey: 'created_at',
    header: 'Signed up',
  },
  {
    accessorKey: 'employer_onboarding_status',
    header: 'Status',
    enableHiding: true,
    cell: ({ row }) => {
      let classes = ''
      let label = ''
      let statusTip = ''

      const employer = row.original as any
      const billableEmployees: number = employer.billable_employees
      const onboardingStatus = row.getValue('status')

      if (billableEmployees > 0 && onboardingStatus === PartnersEmployerStatus.completed) {
        classes = 'bg-green-100 text-green-600'
        label = `${billableEmployees} qualified member${billableEmployees > 1 ? 's' : ''
          }`
      } else if (onboardingStatus === PartnersEmployerStatus.completed) {
        classes = 'bg-yellow-100 text-yellow-800'
        label = 'No qualified members'
      } else {
        classes = 'bg-yellow-100 text-yellow-800'
        label = 'Onboarding'
        statusTip = 'Your client has not completed signing up'
      }

      return (
        <div
          onMouseEnter={statusTip ? (e) => handleStatusHover(e, statusTip) : undefined}
          className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${classes}`}
        >
          {label}
        </div>
      )
    },
  },
  {
    accessorKey: 'earnings',
    header: 'Monthly referral fee',
    cell: ({ row }) => {
      const earnings = row.getValue('earnings') as number
      if (earnings > 0) {
        return formatMoney(earnings)
      } else {
        return 'N/A'
      }
    },
  },
  {
    accessorKey: 'status',
    header: 'Next step',
    enableHiding: true,
    cell: ({ row }) => {
      return (
        <div className='text-gray-900'>
          {nextStepNames[row.getValue('status') as PartnersEmployerStatus]}
        </div>
      )
    },
  }
]

const stepOrder: PartnersEmployerStatus[] = [
  PartnersEmployerStatus.payroll_connected,
  PartnersEmployerStatus.launched,
  PartnersEmployerStatus.quoted,
  PartnersEmployerStatus.quote_accepted,
  PartnersEmployerStatus.bank_account_created,
  PartnersEmployerStatus.completed
]

const stepNames: Partial<Record<PartnersEmployerStatus, string>> = {
  [PartnersEmployerStatus.launched]: 'Launched',
  [PartnersEmployerStatus.quoted]: 'Created quote',
  [PartnersEmployerStatus.quote_accepted]: 'Claimed account',
  [PartnersEmployerStatus.bank_account_created]: 'Connected bank account',
  [PartnersEmployerStatus.completed]: 'Invited employees'
}

const nextStepNames: Partial<Record<PartnersEmployerStatus, string>> = {
  [PartnersEmployerStatus.payroll_connected]: 'Launch onboarding',
  [PartnersEmployerStatus.launched]: 'Create quote',
  [PartnersEmployerStatus.quoted]: 'Claim account',
  [PartnersEmployerStatus.quote_accepted]: 'Connect bank account',
  [PartnersEmployerStatus.bank_account_created]: 'Invite employees',
}
const quoteCreatedStep = stepOrder.indexOf(PartnersEmployerStatus.quoted)

const baseButtonStyles = 'flex flex-col items-start justify-center gap-1 px-4 py-[14px] w-[200px] rounded-xl transition focus:outline-none focus-visible:outline-thatch-500 focus-visible:outline-offset-[-2px]'
const activeStyles = 'bg-thatch-100 border border-thatch-500'
const inactiveStyles = 'bg-white border border-gray-300 hover:bg-gray-100 focus-visible:bg-gray-100'

type PartnerEmployersView = 'active' | 'abandoned'

const DrawerItem = ({ label, children }: { label: string, children: React.ReactNode }) => (
  <>
    <div className='py-2 text-left text-xs font-normal text-gray-500'>{label}</div>
    <div className='pb-4 text-left text-base font-normal text-slate-700'>{children}</div>
  </>)

interface PartnersEmployersTableProps {
  partnerId: string
  partnerAdminId: string
  publicPageURL: string
  canSelfServeQuotes?: boolean
  showReferralPageButtons?: boolean
  canOwnProspects?: boolean
  canReassignProspects?: boolean
  isAdmin?: boolean
  useMultiStepQuoteRequestFlow?: boolean
}

const PAGE_SIZE = 20

function PartnersEmployersTable (
  {
    partnerId,
    partnerAdminId,
    publicPageURL,
    canSelfServeQuotes = false,
    showReferralPageButtons = true,
    canOwnProspects = true,
    canReassignProspects = false,
    isAdmin = false,
    useMultiStepQuoteRequestFlow = false
  }: PartnersEmployersTableProps
) {
  const { assign } = useWindowLocation()

  const [filterAssignees, setFilterAssignees] = useState<Record<string, PartnersPartnerAdmin>>({})
  const [filterStatuses, setFilterStatuses] = useState<Partial<Record<PartnersEmployerStatus, any>>>({})
  const [activeSearchQuery, setActiveSearchQuery] = useState('')
  const [abandonedSearchQuery, setAbandonedSearchQuery] = useState('')
  const [activePagination, setActivePagination] = useState<PaginationState>({ pageIndex: 0, pageSize: PAGE_SIZE })
  const [abandonedPagination, setAbandonedPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: PAGE_SIZE })
  const filters = [filterAssignees, filterStatuses]

  const [tableView, setTableView] = useState<PartnerEmployersView>('active')
  const currentPagination = tableView === 'active' ? activePagination : abandonedPagination

  // Debounce search queries
  const [debouncedActiveSearchQuery] = useDebounceValue(activeSearchQuery, 300)
  const [debouncedAbandonedSearchQuery] = useDebounceValue(abandonedSearchQuery, 300)

  // Build API params for active employers
  const activeParams = useMemo((): GetPartnersEmployersParams => {
    const params: GetPartnersEmployersParams = {
      'filter[in_progress]': false,
      'page[number]': activePagination.pageIndex + 1, // API uses 1-based indexing
      'page[size]': activePagination.pageSize,
    }
    if (debouncedActiveSearchQuery) {
      params['filter[name]'] = debouncedActiveSearchQuery
    }
    return params
  }, [activePagination, debouncedActiveSearchQuery])

  // Build API params for abandoned employers
  const abandonedParams = useMemo((): GetPartnersEmployersParams => {
    const params: GetPartnersEmployersParams = {
      'filter[in_progress]': true,
      'page[number]': abandonedPagination.pageIndex + 1, // API uses 1-based indexing
      'page[size]': abandonedPagination.pageSize,
    }
    if (debouncedAbandonedSearchQuery) {
      params['filter[name]'] = debouncedAbandonedSearchQuery
    }
    if (Object.keys(filterAssignees).length > 0) {
      params['filter[assignee_ids][]'] = Object.keys(filterAssignees)
    }
    if (Object.keys(filterStatuses).length > 0) {
      params['filter[statuses][]'] = Object.keys(filterStatuses)
    }
    return params
  }, [abandonedPagination, debouncedAbandonedSearchQuery, filterAssignees, filterStatuses])

  const { data: activeEmployersData, isLoading: isLoadingActiveEmployers } = useGetPartnersEmployers(activeParams, {
    query: {
      placeholderData: (previousData) => previousData
    }
  })

  const { data: abandonedEmployersData, isLoading: isLoadingAbandonedEmployers, isPending, refetch } = useGetPartnersEmployers(abandonedParams, {
    query: {
      placeholderData: (previousData) => previousData
    }
  })

  const activeEmployers = activeEmployersData?.data ?? []
  const abandonedEmployers = abandonedEmployersData?.data ?? []
  const activePaginationMetadata = activeEmployersData?.pagination
  const abandonedPaginationMetadata = abandonedEmployersData?.pagination

  const isLoading = isLoadingActiveEmployers || isLoadingAbandonedEmployers

  const [isDrawerOpen, setDrawerOpen] = useState(false)
  const [selectedEmployer, setSelectedEmployer] = useState<Employer>()
  const [assigneeInput, setAssigneeInput] = useState('')
  const { data: { data: availableAssignees = [] } = {} } = useGetPartnersPartnerIdPartnerAdmins(partnerId, {
    'filter[name]': assigneeInput,
    'filter[roles][]': ['admin', 'producer'],
    'page[size]': 1000,
    sort: 'first_name,last_name'
  }, {
    query: {
      placeholderData: (previousData) => previousData
    }
  })
  const isAbandonedView = tableView === 'abandoned'
  const {
    name: employerName,
    assignee,
    status,
    last_active_admin_user: employerAdmin,
    quote_link: quoteLink,
    phone_number: phoneNumber
  } = selectedEmployer ?? {}

  const anyFiltersActive = useMemo(() =>
    filters.some(filter => Object.keys(filter).length > 0), filters)

  const updateAssigneeMutation = usePatchPartnersEmployersIdAssignee({
    mutation: {
      onSuccess: (data) => {
        toast.success('Assignment successful!')
        // Reset assignee input to show all assignees in dropdown
        setAssigneeInput('')
        // Update selected employer with new assignee from response
        if (selectedEmployer) {
          setSelectedEmployer({
            ...selectedEmployer,
            assignee: data.assignee
          })
        }
        refetch()
      },
      onError: err => {
        console.error(err)
        toast.error('Could not assign user to client')
      }
    }
  })

  const handleStatusFilterChange = (status: PartnersEmployerStatus) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked
    if (checked) {
      setFilterStatuses({ ...filterStatuses, [status]: true })
    } else {
      const { [status]: _, ...rest } = filterStatuses
      setFilterStatuses(rest)
    }
    // Reset to first page when filters change
    setAbandonedPagination({ ...abandonedPagination, pageIndex: 0 })
  }

  const handleAssigneeFilterChange = (assignee: PartnersPartnerAdmin) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked
    if (checked) {
      setFilterAssignees({ ...filterAssignees, [assignee.id]: assignee })
    } else {
      const { [assignee.id]: _, ...rest } = filterAssignees
      setFilterAssignees(rest)
    }
    // Reset to first page when filters change
    setAbandonedPagination({ ...abandonedPagination, pageIndex: 0 })
  }

  const handleSearchChange = (value: string) => {
    if (tableView === 'active') {
      setActiveSearchQuery(value)
      // Reset to first page when search changes
      setActivePagination({ ...activePagination, pageIndex: 0 })
    } else {
      setAbandonedSearchQuery(value)
      // Reset to first page when search changes
      setAbandonedPagination({ ...abandonedPagination, pageIndex: 0 })
    }
  }

  // Get the current search query based on the active view
  const currentSearchQuery = tableView === 'active' ? activeSearchQuery : abandonedSearchQuery

  const currentStep = useMemo(() => status ? stepOrder.indexOf(status) : 0, [selectedEmployer])

  const data = useMemo(() => {
    return isAbandonedView ? abandonedEmployers : activeEmployers
  }, [activeEmployers, abandonedEmployers, tableView])

  const currentPaginationMetadata = isAbandonedView ? abandonedPaginationMetadata : activePaginationMetadata

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualFiltering: true,
    pageCount: currentPaginationMetadata?.total_pages ?? 0,
    state: {
      pagination: currentPagination,
    },
  })

  useEffect(() => {
    table.getColumn('assignee')?.toggleVisibility(isAdmin && isAbandonedView)
    table.getColumn('status')?.toggleVisibility(isAbandonedView)
    table.getColumn('employer_onboarding_status')?.toggleVisibility(!isAbandonedView)
  }, [tableView, table, isAdmin, isAbandonedView])

  const handleReassign = (assignee?: PartnersPartnerAdmin) => () => {
    updateAssigneeMutation.mutate({
      id: selectedEmployer!.id,
      data: { assignee_id: assignee?.id }
    })
  }

  const PartnersEmployersViewButton = ({ view, label, amount, isLoading }: { view: PartnerEmployersView, label: string, amount: number, isLoading: boolean }) =>
    <button
      type='button'
      onClick={() => setTableView(view)}
      className={`${baseButtonStyles} ${view === tableView ? activeStyles : inactiveStyles}`}
    >
      <div className='text-gray-400 text-xs'>{label}</div>
      {isLoading
        ? <div className='animate-pulse mt-1 h-4 bg-gray-200 rounded w-1/4' />
        : <div className='text-lg font-semibold'>{amount}</div>}
    </button>

  return (
    <div>
      <Sheet open={isDrawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent>
          <SheetHeader className='py-4'>
            <SheetTitle>{employerName}</SheetTitle>
            <SheetDescription hidden>
              Employer details and onboarding progress
            </SheetDescription>
          </SheetHeader>

          <hr />
          <DrawerItem label='Assignee'>
            {canReassignProspects && (isAdmin || assignee?.id === partnerAdminId)
              ? (
                <Autocomplete>
                  <Autocomplete.Input asChild>
                    <TextInput
                      defaultValue={assignee?.name}
                      onChange={event => setAssigneeInput(event.target.value)}
                      placeholder={assignee?.name ?? 'None'}
                    />
                  </Autocomplete.Input>
                  <Autocomplete.List withPortal={false} zIndex={50}>
                    {[undefined, ...availableAssignees].map(assignee => (
                      <Autocomplete.Option
                        key={assignee?.id ?? 'None'}
                        onSelect={handleReassign(assignee)}
                      >
                        {assignee?.name ?? <div className='italic text-gray-500'>None</div>}
                      </Autocomplete.Option>
                    ))}
                  </Autocomplete.List>
                </Autocomplete>
                )
              : assignee?.name ?? '-'}

          </DrawerItem>
          <DrawerItem label='Admin name'>{employerAdmin?.name ?? '-'}</DrawerItem>
          <DrawerItem label='Email'>{employerAdmin?.email ?? '-'}</DrawerItem>
          <DrawerItem label='Phone number'>{phoneNumber ?? '-'}</DrawerItem>
          <DrawerItem label='Last signed in'>{employerAdmin?.last_signed_in ?? '-'}</DrawerItem>
          <DrawerItem label='Completed steps'>
            <StepNav data-testid='step-nav'>
              {stepOrder.slice(1)
                .map((step, i) => (
                  <StepNav.Step
                    key={step}
                    name={stepNames[step]}
                    active={i === currentStep}
                    complete={i < currentStep}
                  >
                    {step === PartnersEmployerStatus.quoted &&
                    currentStep >= quoteCreatedStep &&
                    quoteLink &&
                      <div className='translate-y-[-10px] text-xs'>
                        <TextLink
                          href={quoteLink}
                          color='purple'
                          rightIcon={ExternalLinkIcon}
                        >
                          See quote details
                        </TextLink>
                      </div>}
                  </StepNav.Step>
                ))}
            </StepNav>
          </DrawerItem>
        </SheetContent>
      </Sheet>
      {(isPending || (abandonedPaginationMetadata && abandonedPaginationMetadata.total_records > 0) || anyFiltersActive) && !isLoading &&
        <div className='flex flex-row gap-3 mb-6 w-full'>
          <PartnersEmployersViewButton isLoading={isLoadingActiveEmployers} view='active' label='Active clients' amount={activePaginationMetadata?.total_records ?? 0} />
          <PartnersEmployersViewButton isLoading={isLoadingAbandonedEmployers} view='abandoned' label='Abandoned clients' amount={abandonedPaginationMetadata?.total_records ?? 0} />
        </div>}
      <div className='p-4 rounded-xl bg-white border border-grey-300'>
        {isLoading
          ? <PartnersEmployersTableLoadingSkeleton showReferralPageButtons={showReferralPageButtons} publicPageURL={publicPageURL} />
          : (
            <>
              <div className='flex items-center justify-between mb-3 w-full'>
                <div className='relative w-1/2 max-w-sm mt-2 rounded-md shadow-sm'>
                  <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
                    <Search size={20} color='#9ca3af' />
                  </div>
                  <input
                    id='search-clients'
                    placeholder='Find clients'
                    value={currentSearchQuery}
                    onChange={(event) => {
                      handleSearchChange(event.target.value)
                    }}
                    className='input-default pl-10'
                  />
                </div>
                {showReferralPageButtons && (
                  <div className='flex gap-2'>
                    <ReferralPageLinkButton publicPageURL={publicPageURL} />
                    <CopyButton valueToCopy={publicPageURL} />
                  </div>
                )}
              </div>
              {tableView === 'abandoned' &&
                <div className='flex gap-2 mb-3 w-full'>
                  <FiltersDropdown trigger={<FilterPillButton count={Object.keys(filterAssignees).length} label='Assignee' />}>
                    <FiltersDropdown.Filters>
                      {availableAssignees.map(assignee => (
                        <Checkbox
                          key={assignee.id}
                          label={assignee.name}
                          onChange={handleAssigneeFilterChange(assignee)}
                          value={assignee.id}
                          checked={!!filterAssignees[assignee.id]}
                        />
                      ))}
                    </FiltersDropdown.Filters>
                  </FiltersDropdown>

                  <FiltersDropdown trigger={<FilterPillButton count={Object.keys(filterStatuses).length} label='Status' />}>
                    <FiltersDropdown.Filters>
                      {Object.values(PartnersEmployerStatus)
                        .filter(status => stepNames[status])
                        .map(status => (
                          <Checkbox
                            key={status}
                            label={stepNames[status]}
                            onChange={handleStatusFilterChange(status)}
                            value={status}
                            checked={!!filterStatuses[status]}
                          />
                        ))}
                    </FiltersDropdown.Filters>
                  </FiltersDropdown>
                </div>}
              <div className='-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8'>
                <div className='inline-block min-w-full py-2 align-middle md:px-6 lg:px-8'>
                  <div className='overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg'>
                    <Table id='employers-table' data-testid='employers-table'>
                      <Table.Head>
                        {table.getHeaderGroups().map((headerGroup) => (
                          <Table.Row key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                              <Table.ColumnHeader key={header.id}>{header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}</Table.ColumnHeader>
                            ))}
                          </Table.Row>
                        ))}
                      </Table.Head>
                      <Table.Body>
                        {!isLoading && data.length === 0 && (
                        // The default Slab component does not have great support for zero state, so we're using
                        // a vanilla HTML table row for now
                          <tr className='h-96'>
                            <td
                              colSpan={columns.length}
                              className='text-thatch-gray-secondary text-sm text-center'
                            >
                              You don’t have any clients on Thatch yet.
                              {canOwnProspects && (
                                <>
                                  {canSelfServeQuotes ? ' Create' : ' Request'} a quote to get started.
                                  <div className='flex gap-2 justify-center mt-4'>
                                    <RequestAQuoteButton
                                      isPrimary={false}
                                      location='clients_zero_state'
                                      canSelfServeQuotes={canSelfServeQuotes}
                                      useMultiStepQuoteRequestFlow={useMultiStepQuoteRequestFlow}
                                    />
                                  </div>
                                </>
                              )}
                            </td>
                          </tr>
                        )}
                        {!isLoading && data.length > 0 && (
                          table.getRowModel().rows.map((row) => (
                            <Table.Row
                              key={row.id} accessibleLabel={row.original.name} onClick={() => {
                                if (tableView === 'abandoned') {
                                  setDrawerOpen(true)
                                  const employer = row.original as Employer
                                  setSelectedEmployer(employer)
                                  setAssigneeInput(employer?.assignee?.name ?? '')
                                  return
                                }
                                const id = (row.original as { id: string }).id
                                assign(partnersEmployees.index({ client_id: id }))
                              }}
                            >
                              {row.getVisibleCells().map((cell) => (
                                <Table.Cell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</Table.Cell>
                              ))}
                            </Table.Row>
                          ))
                        )}
                      </Table.Body>
                    </Table>
                  </div>
                </div>
              </div>
              <div className='flex items-center justify-between mt-2 mb-4 px-4'>
                <div className='text-thatch-gray-secondary text-sm'>
                  {currentPaginationMetadata?.total_records ?? 0}{' '}
                  {(currentPaginationMetadata?.total_records ?? 0) === 1
                    ? 'client'
                    : 'clients'}
                </div>
                {currentPaginationMetadata && currentPaginationMetadata.total_records > 0 && (
                  <Pagination
                    pageIndex={currentPagination.pageIndex}
                    perPage={currentPagination.pageSize}
                    totalRows={currentPaginationMetadata.total_records}
                    onPageChange={(pageIndex: number) => {
                      const setter = tableView === 'active' ? setActivePagination : setAbandonedPagination
                      const current = tableView === 'active' ? activePagination : abandonedPagination
                      setter({ ...current, pageIndex })
                    }}
                  />
                )}
              </div>
            </>)}
      </div>
    </div>
  )
}

export default PartnersEmployersTable
